import React, { useState, useMemo, useRef } from 'react';
import { TextField, Typography, Popover, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
//import { myLog } from "../Services/Functions";

function FastTextField (props) {
    const { meta, field, label, type, formik, f_change, f_value, f_touched, f_error, loading, child, required, hidden, startdecorator: StartDecorator, ...rest } = props;

	const [anchorEl, setAnchorEl] = useState(null);
    const inputRef = useRef(null);

    const insertAtCursor = (snippet) => {
        const el = inputRef.current;
        const target = el ?? (document.activeElement instanceof HTMLTextAreaElement || document.activeElement instanceof HTMLInputElement
            ? document.activeElement
            : null);
        if (!target) return;

        const nameKey = meta?.name ?? field;
        const value = formik ? (formik.values?.[nameKey] ?? '') : (props.f_value ?? '');
        const start = target.selectionStart ?? value.length;
        const end   = target.selectionEnd   ?? value.length;

        if (typeof target.setRangeText === 'function') {
            target.setRangeText(snippet, start, end, 'end');
            const next = target.value;

            if (formik) {
                formik.setFieldValue(nameKey, next, true);
            } else if (props.f_change) {
                props.f_change({ target: { name: nameKey, value: next } });
            }

            const pos = start + snippet.length;
            requestAnimationFrame(() => {
                target.focus?.();
                try { target.setSelectionRange?.(pos, pos); } catch {}
            });
            return;
        }

        const before = value.slice(0, start);
        const after  = value.slice(end);
        const next   = before + snippet + after;

        if (formik) {
            formik.setFieldValue(nameKey, next, true);
        } else if (props.f_change) {
            props.f_change({ target: { name: nameKey, value: next } });
        }

        requestAnimationFrame(() => {
            const pos = start + snippet.length;
            target.focus?.();
            try { target.setSelectionRange?.(pos, pos); } catch {}
        });
    };

	const handlePopoverSwitch = (event) => {
		if (anchorEl) {
			setAnchorEl(null);
		}
		else {
			setAnchorEl(event.currentTarget);
		}
	};
	const handlePopoverClose = (event) => {
		setAnchorEl(null);
	};
	const openPopover = Boolean(anchorEl);

	let myType = "text";

    const m_value = useMemo(() => formik?.values?.[meta?.name] ?? '', [formik?.values, meta?.name]);
    const m_error = useMemo(() => formik?.errors?.[meta?.name], [formik?.errors, meta?.name]);
    const m_touched = useMemo(() => formik?.touched?.[meta?.name], [formik?.touched, meta?.name]);

	if (meta) {
		let showHelp = meta.label.length > 30;
		if (type) {
			myType = type	//Overwrite with explicit declared type
		}
		else {
			if (meta.data_type === "decimal" || meta.data_type === "tinyint" || meta.data_type === "smallint") {
				myType = "number";
			}
			if (meta.data_type === "datetime" || meta.data_type === "date") {
				myType = "date";
			}
			if (meta.data_type === "enum") {
				myType = "select";
			}
		}
		return (
			<>
                {StartDecorator &&
                    <StartDecorator
                        addEmoji={insertAtCursor}
                        sx={{ marginBottom: '20px' }}
                    />
                }
				<TextField
					fullWidth
                    inputRef={inputRef}
                    onChange={formik ? formik.handleChange : f_change}
					name={meta.name}
                    key={meta.name}
                    value={formik ? m_value : f_value ?? ''}
					label={meta.label}
                    helperText={
                        formik
                            ? (m_touched && m_error ? m_error : null)
                            : (f_touched && f_error ? f_error : null)
                    }
                    error={formik ? Boolean(m_touched && m_error) : Boolean(f_touched && f_error)}
					required={meta.nullable === "NO"}
					disabled={Boolean(loading)}
					className="styled"
					type={myType}
					sx={{
						width: showHelp ? '85%' : '100%',
						opacity: hidden ? 0 : 1,
					}}
					{...rest}
				>
				{
					child
				}
				</TextField>
				{showHelp &&
					<IconButton
						aria-label="switch popover"
						onClick={handlePopoverSwitch}
						edge="end"
					>
						<InfoOutlinedIcon/>
					</IconButton>
				}
				<Popover
					id="mouse-over-popover"
					sx={{
						pointerEvents: 'none',
					}}
					open={openPopover}
					anchorEl={anchorEl}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
					onClose={handlePopoverClose}
					disableRestoreFocus
				>
					<Typography sx={{ p: 1 }}>{meta.label}</Typography>
				</Popover>
			</>
		);
	}
	if (field) {
		return (
            <>
                {StartDecorator &&
                    <StartDecorator
                        addEmoji={insertAtCursor}
                        sx={{ marginBottom: '20px' }}
                    />
                }
                <TextField
                    fullWidth
                    inputRef={inputRef}
                    onChange={formik ? formik.handleChange : f_change}
                    value={formik ? formik.values[field] : f_value}
                    helperText={formik ?
                        (formik.touched[field] && formik.errors[field] ? formik.errors[field] : null)
                        :
                        (f_touched && f_error ? f_error : null)
                    }
                    error={formik ? Boolean(formik.touched[field] && formik.errors[field]) : Boolean(f_touched && f_error)}
                    label={label}
                    name={field}
                    key={field}
                    required={required}
                    disabled={Boolean(loading)}
                    className="styled"
                    type={type || 'text'}
                    {...rest}
                >
                {
                    child
                }
                </TextField>
            </>
		);
	}
}

function areEqual(prevProps, nextProps) {
    const prevName = prevProps.meta?.name ?? prevProps.field;
    const nextName = nextProps.meta?.name ?? nextProps.field;

    if (prevName !== nextName) return false;

    const formikEqual =
        prevProps.formik?.values?.[prevName] === nextProps.formik?.values?.[nextName] &&
        prevProps.formik?.errors?.[prevName] === nextProps.formik?.errors?.[nextName] &&
        prevProps.formik?.touched?.[prevName] === nextProps.formik?.touched?.[nextName];

    const childEqual = prevProps.isDinamic !== true ? true : prevProps.child === nextProps.child;
    const loadingEqual = prevProps.loading === nextProps.loading;

    return (

        childEqual &&
        loadingEqual &&
        formikEqual
    );
}

export default React.memo(FastTextField, areEqual);
